import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { handleApi } from "../../apis";
import { GET_TRANSACTION_LIST_V2 } from "../../apis/configuration";
import { useSnackbar } from "../../context/SnackbarContext";
import { useWalletContext } from "../../context/WalletContext";
import useDebouncedValue from "../useDebouncedValue";
import { getStartAndEndDate } from "../../utils";
import { DateFilter } from "../../constants";

export const initialListingFilters = {
  view: "contact",
  dateType: DateFilter[0],
  startDate: null,
  endDate: null,
  fromAmount: null,
  toAmount: null,
  companies: [],
  txnSubTypes: [],
};

const PAGE_SIZE = 20;

const useTransactionListing = ({ txnTypes = [], customerPhone = null, initialFilters = null, vendor_id, seedData = null }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { txnFilterOptionsCache, cacheTxnFilterOptions } = useWalletContext();

  const hasSeed = Boolean(seedData?.transactions?.length);

  // Company/Type options are the same for every listing of this txn-type group
  // (e.g. all Easyload contacts share the same telco/sub-type universe), so they're
  // cached in WalletContext and reused instead of refetching on every mount.
  const filterCacheKey = txnTypes.length ? [...txnTypes].sort().join(",") : "all";
  const cachedFilterOptions = txnFilterOptionsCache?.[filterCacheKey];

  const [searchQ, setSearchQ] = useState("");
  const debounceSearchName = useDebouncedValue(searchQ, 500);

  const [filters, setFilters] = useState(
    initialFilters ? { ...initialListingFilters, ...initialFilters } : initialListingFilters
  );
  const [transactions, setTransactions] = useState(hasSeed ? seedData.transactions : []);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(hasSeed ? Boolean(seedData.has_next) : false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!hasSeed);
  const [isError, setIsError] = useState(false);
  const [companyOptions, setCompanyOptions] = useState(() => cachedFilterOptions?.companies || []);
  const [typeOptions, setTypeOptions] = useState(() => cachedFilterOptions?.txnSubTypes || []);

  const cancelToken = useRef();
  const filtersRef = useRef(filters);
  const nextIdRef = useRef(hasSeed && seedData?.next_id != null ? String(seedData.next_id) : "");
  const nextLastTxnAtRef = useRef(hasSeed && seedData?.next_last_transaction_at != null ? seedData.next_last_transaction_at : null);
  const companiesInitRef = useRef(Boolean(cachedFilterOptions?.companies?.length));
  const typesInitRef = useRef(Boolean(cachedFilterOptions?.txnSubTypes?.length));
  const transactionsRef = useRef(hasSeed ? seedData.transactions : []);
  const lastFetchSignatureRef = useRef(null);
  const isSeededRef = useRef(hasSeed);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  const cancelPrevious = () => {
    if (cancelToken.current)
      cancelToken.current.cancel("New request appears, canceling previous request.");
    cancelToken.current = axios.CancelToken.source();
  };

  const fetchList = useCallback(async ({ page: p, search, filterObj, reset }) => {
    setIsLoading(true);
    let canceled = false;
    let failed = false;
    try {
      const params = { page: p, limit: PAGE_SIZE };
      const filter = {};

      if (txnTypes.length) filter.txn_type = txnTypes;
      if (customerPhone) params.customer_phone = customerPhone;
      if (vendor_id) params.vendor_id = vendor_id;
      if (search) params.search = search;
      if (!reset && nextIdRef.current) params.next_id = nextIdRef.current;
      if (!reset && nextLastTxnAtRef.current) params.next_last_transaction_at = nextLastTxnAtRef.current;
      if (filterObj.startDate) params.start_date = moment(filterObj.startDate).format("YYYY-MM-DD");
      if (filterObj.endDate) params.end_date = moment(filterObj.endDate).format("YYYY-MM-DD");
      if (filterObj.fromAmount != null || filterObj.toAmount != null) {
        filter.amount = {};
        if (filterObj.fromAmount != null) filter.amount.min = filterObj.fromAmount;
        if (filterObj.toAmount != null) filter.amount.max = filterObj.toAmount;
      }
      if (filterObj.companies?.length) filter.companies = filterObj.companies;
      if (filterObj.txnSubTypes?.length) filter.txn_sub_types = filterObj.txnSubTypes;

      params.filter = filter;
      params.include_totals = reset;

      const data = await handleApi(GET_TRANSACTION_LIST_V2, params, cancelToken.current, navigate);

      if (data?.success) {
        const incoming = data.transactions || [];
        setTransactions((prev) => (reset ? incoming : [...prev, ...incoming]));
        setHasNext(Boolean(data.has_next));
        nextIdRef.current = data.next_id != null ? String(data.next_id) : "";
        nextLastTxnAtRef.current = data.next_last_transaction_at != null ? data.next_last_transaction_at : null;

        if (reset) {
          setTotalCount(data.total_count || data.total_bills_count || 0);

          if (!companiesInitRef.current) {
            const rawCompanies = data.filters?.companies || [];
            if (rawCompanies.length) {
              const options = rawCompanies.map((c) => ({ label: c, value: c }));
              setCompanyOptions(options);
              companiesInitRef.current = true;
              cacheTxnFilterOptions(filterCacheKey, { companies: options });
            }
          }

          if (!typesInitRef.current) {
            const rawTypes = data.filters?.txn_sub_types || [];
            if (rawTypes.length) {
              const options = rawTypes.map((t) => ({ label: t, value: t }));
              setTypeOptions(options);
              typesInitRef.current = true;
              cacheTxnFilterOptions(filterCacheKey, { txnSubTypes: options });
            }
          }
        }
      } else {
        failed = true;
      }
    } catch (error) {
      if (error.message === "New request appears, canceling previous request.") {
        canceled = true;
        return;
      }
      failed = true;
      showSnackbar({ message: error?.response?.data?.detail || error?.response?.data?.message || "Error fetching transactions", severity: "error" });
    } finally {
      setIsLoading(false);
      // Don't hide the spinner if this request was canceled — the new request is still in flight
      if (!canceled) {
        setIsInitialLoading(false);
        setIsError(failed);
      }
    }
  }, [txnTypes, customerPhone]);

  const updateTransactionItem = useCallback((referenceId, updates) => {
    setTransactions((prev) =>
      prev.map((t) => (t.reference_id === referenceId ? { ...t, ...updates } : t))
    );
  }, []);

  const getFilterSignature = (filt, srch) =>
    JSON.stringify({
      dateType: filt.dateType,
      startDate: filt.startDate,
      endDate: filt.endDate,
      fromAmount: filt.fromAmount,
      toAmount: filt.toAmount,
      companies: filt.companies,
      txnSubTypes: filt.txnSubTypes,
      search: srch,
    });

  const resetAndFetch = useCallback((overrideFilters, overrideSearch) => {
    const filt = overrideFilters ?? filtersRef.current;
    const srch = overrideSearch !== undefined ? overrideSearch : debounceSearchName;
    lastFetchSignatureRef.current = getFilterSignature(filt, srch);
    cancelPrevious();
    nextIdRef.current = "";
    nextLastTxnAtRef.current = null;
    setTransactions([]);
    setPage(1);
    setHasNext(false);
    setIsInitialLoading(true);
    fetchList({ page: 1, search: srch, filterObj: filt, reset: true });
  }, [debounceSearchName, fetchList]);

  useEffect(() => {
    if (isSeededRef.current) {
      isSeededRef.current = false;
      // Seed data (e.g. a contact's recent_transactions preview) has no filter
      // metadata of its own — only refetch if Company/Type options aren't already
      // cached in WalletContext from an earlier listing of this txn-type group.
      const hasCachedFilterOptions = companiesInitRef.current || typesInitRef.current;
      if (!hasCachedFilterOptions && filtersRef.current.view === "transaction") {
        resetAndFetch(filtersRef.current, debounceSearchName);
      }
      return;
    }
    if (filtersRef.current.view !== "transaction") return;
    resetAndFetch(filtersRef.current, debounceSearchName);
  }, [debounceSearchName]);

  const handleLoadMore = useCallback(() => {
    if (!hasNext || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchList({ page: nextPage, search: debounceSearchName, filterObj: filtersRef.current, reset: false });
  }, [hasNext, isLoading, page, debounceSearchName, fetchList]);

  const handleFilterChange = useCallback((key, value) => {
    const current = filtersRef.current;
    const next = { ...current };
    const k = String(key).toLowerCase();

    if (k === "view") {
      next.view = value || "contact";
      setFilters(next);
      if (next.view === "transaction") {
        const sig = getFilterSignature(next, debounceSearchName);
        if (transactionsRef.current.length > 0 && sig === lastFetchSignatureRef.current) return;
        resetAndFetch(next, debounceSearchName);
      }
      return;
    }
    if (k === "keywords") {
      setSearchQ(value ?? "");
      return;
    }
    if (k === "date") {
      if (value?.startDate || value?.endDate) {
        next.dateType = "Custom";
        next.startDate = value.startDate ?? null;
        next.endDate = value.endDate ?? null;
      } else {
        next.dateType = DateFilter[0];
        next.startDate = null;
        next.endDate = null;
      }
    } else if (k === "amount") {
      next.fromAmount = value?.min !== "" && value?.min != null ? Number(value.min) : null;
      next.toAmount = value?.max !== "" && value?.max != null ? Number(value.max) : null;
    } else if (k === "companies") {
      next.companies = Array.isArray(value) ? value.filter(Boolean) : [];
    } else if (k === "type") {
      next.txnSubTypes = Array.isArray(value) ? value.filter(Boolean) : [];
    }

    setFilters(next);
    resetAndFetch(next, debounceSearchName);
  }, [debounceSearchName, resetAndFetch]);

  const handleRemoveChip = useCallback((chip) => {
    const current = filtersRef.current;
    const id = String(chip.id ?? chip.label).toLowerCase();

    if (id === "keywords" || id === "search") {
      setSearchQ("");
      return;
    }
    if (id === "view" || id === "transaction" || id === "contact") {
      setFilters((prev) => ({ ...prev, view: "contact" }));
      return;
    }

    const next = { ...current };
    if (id === "date") {
      next.dateType = DateFilter[0];
      next.startDate = null;
      next.endDate = null;
    } else if (id === "amount") {
      next.fromAmount = null;
      next.toAmount = null;
    } else if (id === "company" || id === "companies") {
      next.companies = [];
    } else if (id === "type") {
      next.txnSubTypes = [];
    }

    setFilters(next);
    resetAndFetch(next, debounceSearchName);
  }, [debounceSearchName, resetAndFetch]);

  return {
    searchQ,
    setSearchQ,
    debounceSearchName,
    filters,
    setFilters,
    transactions,
    page,
    hasNext,
    totalCount,
    isLoading,
    isInitialLoading,
    isError,
    companyOptions,
    typeOptions,
    handleLoadMore,
    handleFilterChange,
    handleRemoveChip,
    resetAndFetch,
    updateTransactionItem,
  };
};

export default useTransactionListing;
